/**
 * Reusable SMS quick-send dialog.
 *
 * Caller passes a list of `recipients` (each: { to, name, first_name,
 * appointment_id, appointment_type, appointment_datetime }) and optionally a
 * default message body or template. The dialog handles template loading,
 * per-recipient merge-field substitution, sending, and showing the results.
 *
 * Used inline from Appointments (single client) and Schedule (whole class).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Send, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

const MERGE_HINTS = ["{first_name}", "{name}", "{class_time}", "{class_type}"];

function applyMerge(body, fields) {
  return (body || "").replace(/\{([a-zA-Z0-9_]+)\}/g, (_, k) =>
    fields?.[k] == null ? "" : String(fields[k]),
  );
}

function classTimeStr(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function SmsQuickSendDialog({
  open,
  onClose,
  recipients,        // array of recipient objects
  title = "Send SMS",
  defaultBody = "",
  source = "inline", // logged for audit
}) {
  const [body, setBody] = useState(defaultBody);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [checked, setChecked] = useState({}); // {to: bool}
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setBody(defaultBody);
    setSelectedTpl("");
    setResults(null);
    // pre-tick every valid recipient
    const init = {};
    (recipients || []).forEach((r) => {
      if (r.valid !== false && r.to) init[r.to] = true;
    });
    setChecked(init);
  }, [open, recipients, defaultBody]);

  // Load templates once dialog opens
  useEffect(() => {
    if (!open) return;
    api.get("/sms/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, [open]);

  function applyTemplate(tid) {
    setSelectedTpl(tid);
    const t = templates.find((x) => x.id === tid);
    if (t) setBody(t.body);
  }

  const selected = useMemo(
    () => (recipients || []).filter((r) => r.to && checked[r.to]),
    [recipients, checked],
  );

  async function send() {
    if (!body.trim()) {
      toast.error("Message body is empty");
      return;
    }
    if (selected.length === 0) {
      toast.error("Pick at least one recipient");
      return;
    }
    setSending(true);
    try {
      const messages = selected.map((r) => ({
        to: r.to,
        body: applyMerge(body, {
          first_name: r.first_name || (r.name || "").split(" ")[0] || "",
          name: r.name || "",
          class_time: classTimeStr(r.appointment_datetime),
          class_type: r.appointment_type || "",
        }),
        recipient_name: r.name,
        appointment_id: r.appointment_id,
        customer_key: r.customer_key,
      }));
      const r = await api.post("/sms/send", { messages, source });
      setResults(r.data);
      const sent = r.data?.sent_count ?? 0;
      const failed = r.data?.failed_count ?? 0;
      if (failed === 0) {
        toast.success(`Sent ${sent} SMS`, {
          description: r.data?.total_price != null ? `Cost: $${r.data.total_price.toFixed(2)}` : undefined,
        });
      } else {
        toast.warning(`Sent ${sent}, failed ${failed}`, {
          description: "See per-recipient details below.",
        });
      }
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="sms-quick-send-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {!results ? (
          <div className="space-y-4">
            {/* Template picker */}
            {templates.length > 0 && (
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Template (optional)</Label>
                <Select value={selectedTpl} onValueChange={applyTemplate}>
                  <SelectTrigger className="border-zinc-300 mt-1.5" data-testid="sms-template-select">
                    <SelectValue placeholder="Pick a saved template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Message body */}
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Message</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                className="border-zinc-300 mt-1.5 font-mono-num text-sm"
                placeholder="Hi {first_name}, just a reminder…"
                data-testid="sms-body-textarea"
              />
              <div className="mt-1 flex flex-wrap gap-1 text-2xs">
                <span className="text-zinc-500">Merge fields:</span>
                {MERGE_HINTS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setBody((b) => b + h)}
                    className="font-mono-num text-zinc-700 hover:text-zinc-950 hover:underline"
                  >
                    {h}
                  </button>
                ))}
                <span className="ml-auto font-mono-num text-zinc-500">{body.length} chars · ~{Math.max(1, Math.ceil(body.length / 160))} part(s)</span>
              </div>
            </div>

            {/* Recipients */}
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                Recipients ({selected.length} of {(recipients || []).length})
              </Label>
              <div className="mt-1.5 max-h-56 overflow-y-auto rounded-md border border-zinc-200 divide-y divide-zinc-100">
                {(recipients || []).length === 0 && (
                  <div className="p-3 text-sm text-zinc-500">No recipients.</div>
                )}
                {(recipients || []).map((r, i) => {
                  const id = r.to || `bad-${i}`;
                  const disabled = !r.to;
                  return (
                    <div key={id} className="p-2 flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={!!checked[r.to]}
                        disabled={disabled}
                        onCheckedChange={(v) => setChecked((c) => ({ ...c, [r.to]: !!v }))}
                        data-testid={`sms-recipient-${i}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-zinc-900 truncate">
                          {r.name || "—"}
                          {r.appointment_type && <span className="ml-2 text-2xs text-zinc-500">{r.appointment_type}</span>}
                        </div>
                        <div className="font-mono-num text-2xs text-zinc-500 truncate">
                          {r.to || r.raw_phone || "(no phone)"} {r.appointment_datetime && `· ${classTimeStr(r.appointment_datetime)}`}
                        </div>
                      </div>
                      {!r.to && (
                        <span className="inline-flex items-center gap-1 text-2xs text-amber-700">
                          <AlertTriangle className="h-3 w-3" /> {r.reason || "no phone"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            <div className="text-sm text-zinc-700">
              Sent <span className="font-semibold">{results.sent_count}</span> · Failed{" "}
              <span className="font-semibold">{results.failed_count}</span>
              {results.total_price != null && <> · Cost <span className="font-mono-num">${results.total_price.toFixed(2)}</span></>}
              {results.balance != null && <> · Balance <span className="font-mono-num">${results.balance.toFixed(2)}</span></>}
            </div>
            <div className="rounded-md border border-zinc-200 divide-y divide-zinc-100">
              {(results.results || []).map((m, i) => {
                const ok = (m.status || "").toUpperCase() === "SUCCESS";
                return (
                  <div key={i} className="p-2 flex items-center gap-2 text-sm">
                    {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                    <span className="font-mono-num">{m.to}</span>
                    <span className={"ml-auto text-xs " + (ok ? "text-emerald-700" : "text-amber-700")}>
                      {m.status || "—"}{m.error_text ? ` · ${m.error_text}` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          {!results ? (
            <>
              <Button variant="outline" onClick={onClose} data-testid="sms-cancel-btn">Cancel</Button>
              <Button
                onClick={send}
                disabled={sending || selected.length === 0 || !body.trim()}
                className="bg-zinc-950 hover:bg-zinc-800 text-white"
                data-testid="sms-send-btn"
              >
                <Send className="h-4 w-4 mr-1.5" />
                {sending ? "Sending…" : `Send to ${selected.length || ""}`.trim()}
              </Button>
            </>
          ) : (
            <Button onClick={onClose} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="sms-close-btn">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
